import { useState, useEffect } from "react";
import {
  listCategories,
  getProductCategories,
  putProductCategories,
  type CatalogCategoryOut,
} from "../../../api/catalog";
import { Button } from "../../primitives/Button";

type ProductCategoriesTabProps = {
  productId: string;
};

export function ProductCategoriesTab({ productId }: ProductCategoriesTabProps) {
  const [allCategories, setAllCategories] = useState<CatalogCategoryOut[]>([]);
  const [productCategoryIds, setProductCategoryIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [all, assigned] = await Promise.all([
        listCategories({ kind: "parts" }),
        getProductCategories(productId),
      ]);
      setAllCategories(all);
      setProductCategoryIds(new Set(assigned.map((c) => c.id)));
    } catch (e) {
      console.error("Failed to load categories", e);
      setAllCategories([]);
      setProductCategoryIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [productId]);

  const toggle = (id: string) => {
    setProductCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await putProductCategories(productId, Array.from(productCategoryIds));
      await load();
    } catch (e) {
      alert((e as Error).message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <p className="text-sm text-muted">
        Sélectionnez les catégories (pièces) auxquelles ce produit appartient.
      </p>
      <div className="flex flex-wrap gap-2">
        {allCategories.map((cat) => (
          <label
            key={cat.id}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 cursor-pointer hover:bg-surface-muted/50"
          >
            <input
              type="checkbox"
              checked={productCategoryIds.has(cat.id)}
              onChange={() => toggle(cat.id)}
              className="h-4 w-4 rounded border-border"
            />
            <span className="text-sm text-text">{cat.name}</span>
            <span className="text-xs text-muted">({cat.slug})</span>
          </label>
        ))}
      </div>
      {allCategories.length === 0 && (
        <p className="text-sm text-muted">Aucune catégorie de type &quot;parts&quot;. Créez-en dans le catalogue.</p>
      )}
      <div className="pt-2">
        <Button
          label={saving ? "Enregistrement…" : "Enregistrer les catégories"}
          intent="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </div>
  );
}
